import gsap from "gsap";

import { prefersReducedMotion } from "../utils/variables";

export let cursor: {
    addState(state: string): void;
    removeState(state: string): void;
    setText(text: string): void;
};

const trailCount = 2;
const trailDelay = 70;
const cursorSizeStates = ["-link", "-minor-link", "-medium-link", "-major-link", "-video", "-close", "-drag"] as const;

let cursorEl: HTMLElement | null = null;
let textEl: HTMLElement | null = null;
let linkHoverActive = false;
let currentCursorText = "";
let activeDataCursorState = "";
let mouseX = 0;
let mouseY = 0;
let textSuppressed = false;
let pageLoading = false;

interface PosRecord {
    x: number;
    y: number;
    t: number;
}

function rawAddState(state: string): void {
    if (!cursorEl) return;

    const tokens = state
        .split(/\s+/)
        .filter(Boolean)
        .filter(token => !pageLoading || (!isSizeState(token) && token !== "-active"));
    if (tokens.length) cursorEl.classList.add(...tokens);
}

function rawRemoveState(state: string): void {
    if (!cursorEl) return;

    const tokens = state.split(/\s+/).filter(Boolean);
    if (tokens.length) cursorEl.classList.remove(...tokens);
}

function rawSetText(text: string): void {
    if (!textEl || !cursorEl) return;

    textEl.textContent = text;
    cursorEl.classList.toggle("-text", text.length > 0 && !pageLoading);
}

function toStateTokens(state: string): string[] {
    return state
        .split(/\s+/)
        .map(token => token.trim())
        .filter(Boolean);
}

function isSizeState(state: string): boolean {
    return (cursorSizeStates as readonly string[]).includes(state);
}

function applyDataCursorState(state: string): void {
    const tokens = toStateTokens(state);
    const sizeTokens = tokens.filter(isSizeState);
    const requestedSizeState = sizeTokens.length ? sizeTokens[sizeTokens.length - 1] : "";
    const previousTokens = toStateTokens(activeDataCursorState);

    previousTokens.forEach(token => {
        if (!tokens.includes(token)) rawRemoveState(token);
    });

    cursorSizeStates.forEach(s => {
        if (s !== requestedSizeState) rawRemoveState(s);
    });

    const nonSizeTokens = tokens.filter(token => !isSizeState(token));
    if (nonSizeTokens.length) rawAddState(nonSizeTokens.join(" "));
    if (requestedSizeState) rawAddState(requestedSizeState);

    activeDataCursorState = state;
}

function elementProvidesOwnText(el: HTMLElement): boolean {
    return el.hasAttribute("data-cursor-text") || !!el.querySelector("[data-cursor-text]");
}

function suppressTextZone(): void {
    if (textSuppressed) return;
    textSuppressed = true;
    rawSetText("");
}

function restoreTextZone(): void {
    if (!textSuppressed) return;
    textSuppressed = false;
    rawSetText(currentCursorText);
}

export function resetCursorState() {
    if (!cursorEl || !textEl) return;

    toStateTokens(activeDataCursorState).forEach(token => rawRemoveState(token));
    cursorSizeStates.forEach(s => rawRemoveState(s));
    hoverElements.forEach(({ cursorClass }) => rawRemoveState(cursorClass));
    rawRemoveState("-active");

    activeDataCursorState = "";
    currentCursorText = "";
    linkHoverActive = false;
    textSuppressed = false;
    rawSetText("");
}

export function setCursorPageLoading(loading: boolean) {
    if (!cursorEl || pageLoading === loading) return;

    resetCursorState();
    pageLoading = loading;
    cursorEl.classList.toggle("-loading", loading);
    recomputeCursorState();
}

export function recomputeCursorState() {
    if (!cursorEl) return;
    const target = document.elementFromPoint(mouseX, mouseY);
    if (!target || !(target instanceof Element)) return;

    for (const { selector, cursorClass } of hoverElements) {
        const matchedEl = target.closest<HTMLElement>(selector);
        if (!matchedEl) continue;

        cursor.addState(cursorClass);
        if (isSizeState(cursorClass)) {
            linkHoverActive = true;

            if (!elementProvidesOwnText(matchedEl)) {
                suppressTextZone();
            }

            if (matchedEl.hasAttribute("data-cursor")) {
                applyDataCursorState(matchedEl.getAttribute("data-cursor") ?? "");
            }
        }
    }

    if (!linkHoverActive && !isHoverElementTarget(target)) {
        const dataCursorEl = getDataCursorElement(target);
        applyDataCursorState(dataCursorEl?.getAttribute("data-cursor") ?? "");
    }

    setCursorTextFromElement(getTextTriggerElement(target));
}

export default function initCursor() {
    if (cursorEl) return;

    const el = document.createElement("div");
    el.className = "mf-cursor -hidden";
    el.setAttribute("aria-hidden", "true");

    const txt = document.createElement("div");
    txt.className = "mf-cursor-text";
    el.appendChild(txt);

    document.body.appendChild(el);
    cursorEl = el;
    textEl = txt;

    cursor = {
        addState(state: string) {
            const tokens = toStateTokens(state);
            const requestedSizeStates = tokens.filter(isSizeState);

            if (requestedSizeStates.length) {
                if (linkHoverActive) {
                    const nonSizeTokens = tokens.filter(t => !isSizeState(t));
                    if (nonSizeTokens.length) rawAddState(nonSizeTokens.join(" "));
                    return;
                }

                const winner = requestedSizeStates[requestedSizeStates.length - 1];
                cursorSizeStates.forEach(s => {
                    if (s !== winner) rawRemoveState(s);
                });
            }

            rawAddState(state);
        },

        removeState(state: string) {
            rawRemoveState(state);
        },

        setText(text: string) {
            const nextText = typeof text === "string" ? text : "";
            currentCursorText = nextText;

            if (!textSuppressed) {
                rawSetText(nextText);
            }
        },
    };

    let started = false;
    const xTo = gsap.quickTo(el, "x", { duration: 0.3, ease: "expo.out" });
    const yTo = gsap.quickTo(el, "y", { duration: 0.3, ease: "expo.out" });

    document.addEventListener("mousemove", (e: MouseEvent) => {
        mouseX = e.clientX;
        mouseY = e.clientY;

        const isInstant = !started || prefersReducedMotion;
        xTo(mouseX, isInstant ? mouseX : undefined);
        yTo(mouseY, isInstant ? mouseY : undefined);

        if (!started) {
            started = true;
            el.classList.remove("-hidden");
        }
    });

    document.addEventListener("mouseleave", () => el.classList.add("-hidden"));
    document.addEventListener("mouseenter", () => el.classList.remove("-hidden"));
    document.addEventListener("mousedown", () => {
        rawAddState("-active");
        if (prefersReducedMotion || el.classList.contains("-drag") || el.classList.contains("-close")) return;

        gsap.to(el, { scale: 0.8, duration: 0.2, ease: "expo.out", overwrite: "auto" });
    });
    document.addEventListener("mouseup", () => {
        rawRemoveState("-active");
        if (prefersReducedMotion) return;

        gsap.to(el, { scale: 1, duration: 0.5, ease: "back.out(3)", overwrite: "auto" });
    });

    initHovers();
    initCursorTextHandoff();
    initDataCursorZoneHandoff();
    initTrail();
}

const hoverElements = [
    { selector: "a, button, .-hover-link, .track, .filter-group", cursorClass: "-minor-link" },
    { selector: "img, video, .header--white, .track, .-hover-white", cursorClass: "-white" },
];

function getDataCursorElement(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>("[data-cursor]");
}

function isHoverElementTarget(target: Element): boolean {
    return hoverElements.filter(({ cursorClass }) => isSizeState(cursorClass)).some(({ selector }) => target.closest(selector) !== null);
}

function initHovers() {
    document.addEventListener("mouseover", event => {
        const target = event.target as Element;
        const from = (event as MouseEvent).relatedTarget as Element | null;

        for (const { selector, cursorClass } of hoverElements) {
            const matchedEl = target.closest<HTMLElement>(selector);
            if (!matchedEl || matchedEl.contains(from)) continue;

            cursor.addState(cursorClass);
            if (isSizeState(cursorClass)) {
                linkHoverActive = true;

                if (!elementProvidesOwnText(matchedEl)) {
                    suppressTextZone();
                }
            }

            if (isSizeState(cursorClass) && matchedEl.hasAttribute("data-cursor")) {
                applyDataCursorState(matchedEl.getAttribute("data-cursor") ?? "");
            }
        }
    });

    document.addEventListener("mouseout", event => {
        const target = event.target as Element;
        const to = (event as MouseEvent).relatedTarget as Element | null;

        for (const { selector, cursorClass } of hoverElements) {
            const matchedEl = target.closest<HTMLElement>(selector);
            if (!matchedEl || matchedEl.contains(to)) continue;

            cursor.removeState(cursorClass);
            if (isSizeState(cursorClass)) {
                linkHoverActive = false;

                const dest = getDataCursorElement(to);
                applyDataCursorState(dest?.getAttribute("data-cursor") ?? "");
                restoreTextZone();
            }
        }
    });
}

function initDataCursorZoneHandoff() {
    document.addEventListener("mouseover", event => {
        if (linkHoverActive) return;
        if (isHoverElementTarget(event.target as Element)) return;

        const targetEl = getDataCursorElement(event.target);
        applyDataCursorState(targetEl?.getAttribute("data-cursor") ?? "");
    });

    document.addEventListener("mouseout", event => {
        if (linkHoverActive) return;

        const fromEl = getDataCursorElement(event.target);
        if (!fromEl) return;

        const toEl = getDataCursorElement((event as MouseEvent).relatedTarget);
        if (toEl === fromEl) return;

        applyDataCursorState(toEl?.getAttribute("data-cursor") ?? "");
    });
}

function getTextTriggerElement(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>("[data-cursor-text]");
}

function setCursorTextFromElement(el: HTMLElement | null): void {
    const previousText = currentCursorText;
    const nextText = el?.getAttribute("data-cursor-text") ?? "";
    if (nextText === previousText) return;

    cursor.setText(nextText);
    currentCursorText = nextText;
}

function initCursorTextHandoff() {
    document.addEventListener("mouseover", event => {
        const targetEl = getTextTriggerElement(event.target);
        if (!targetEl) return;

        setCursorTextFromElement(targetEl);
    });

    document.addEventListener("mouseout", event => {
        const fromEl = getTextTriggerElement(event.target);
        if (!fromEl) return;

        const toEl = getTextTriggerElement((event as MouseEvent).relatedTarget);
        if (toEl === fromEl) return;

        setCursorTextFromElement(toEl);
    });
}

const trailEls: HTMLElement[] = [];
const posHistory: PosRecord[] = [];
const trailFadeMs = 400;

let trailVisibleUntil = 0;

function getHistoryPosition(targetTime: number): { x: number; y: number } | null {
    if (!posHistory.length) return null;

    const last = posHistory[posHistory.length - 1];
    if (targetTime >= last.t) return { x: last.x, y: last.y };

    const first = posHistory[0];
    if (targetTime <= first.t) return { x: first.x, y: first.y };

    let lo = 0,
        hi = posHistory.length - 1;

    while (lo < hi - 1) {
        const mid = (lo + hi) >>> 1;
        if (posHistory[mid].t <= targetTime) lo = mid;
        else hi = mid;
    }

    const a = posHistory[lo];
    const b = posHistory[hi];
    const frac = (targetTime - a.t) / (b.t - a.t);
    return {
        x: a.x + (b.x - a.x) * frac,
        y: a.y + (b.y - a.y) * frac,
    };
}

function initTrail() {
    for (let i = 0; i < trailCount; i++) {
        const el = document.createElement("div");
        el.className = "cursor-trail";
        el.setAttribute("aria-hidden", "true");
        document.body.appendChild(el);

        gsap.set(el, { xPercent: -50, yPercent: -50, rotation: 45, scale: 1.6 });
        trailEls.push(el);
    }

    document.addEventListener("mousemove", (e: MouseEvent) => {
        const now = Date.now();
        posHistory.push({ x: e.clientX, y: e.clientY, t: now });

        const cutoff = now - trailDelay * trailCount * 4;
        while (posHistory.length > 1 && posHistory[0].t < cutoff) posHistory.shift();
    });

    if (cursorEl) {
        const el = cursorEl;

        new MutationObserver(() => {
            const isLink = el.classList.contains("-link");
            const isWhite = el.classList.contains("-white");

            trailVisibleUntil = isLink ? Infinity : Math.min(trailVisibleUntil, Date.now() + trailFadeMs);

            trailEls.forEach((trailEl, i) => {
                trailEl.classList.toggle("-white", isWhite);
                trailEl.style.opacity = isLink && !prefersReducedMotion ? String((1 - i / trailCount) * 0.8) : "0";
            });
        }).observe(el, { attributes: true, attributeFilter: ["class"] });
    }

    const trailSetters = trailEls.map(el => ({ x: gsap.quickSetter(el, "x", "px"), y: gsap.quickSetter(el, "y", "px") }));

    gsap.ticker.add(() => {
        const now = Date.now();
        if (now > trailVisibleUntil) return;

        trailSetters.forEach((setter, i) => {
            const pos = getHistoryPosition(now - (i + 1) * trailDelay);
            if (!pos) return;

            setter.x(pos.x);
            setter.y(pos.y);
        });
    });
}
